import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api, { refreshAccessToken, bootstrapAccessToken, getAccessToken } from '../services/api';
import Loading from '../components/Loading';
import PlatformConfirmModal from '../components/PlatformConfirmModal';
import { getApiUrl } from '../config/runtimeConfig';

const GAME_ICONS = { whot: '🎴' };
const GAME_NAMES = { whot: 'WHOT' };

const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

export default function StakePage() {
  const [playerCount, setPlayerCount] = useState(2);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joiningTier, setJoiningTier] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [tournamentNow, setTournamentNow] = useState(Date.now());
  const playerCountRef = useRef(playerCount);
  playerCountRef.current = playerCount;

  // Inline joined state (replaces the old /waiting page)
  const [joinedTierId, setJoinedTierId] = useState(null);
  const [activeMatchId, setActiveMatchId] = useState(null);
  const [joinedPlayersCurrent, setJoinedPlayersCurrent] = useState(1);
  const [joinedPlayersNeeded, setJoinedPlayersNeeded] = useState(2);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirmTierId, setCancelConfirmTierId] = useState(null);

  const getWaitingLabel = useCallback((current, needed) => {
    const total = Number(needed) || 2;
    const count = Number(current) || 0;
    if (count >= total) return 'READY TO START';
    if (total === 2 && count === 1) return 'WAITING FOR OPPONENT';
    return 'WAITING FOR PLAYERS';
  }, []);

  const navigate = useNavigate();
  const socketRef = useRef(null);
  const joinedTierRef = useRef(null);
  const navigatingRef = useRef(false);
  const matchTransitionTimeoutRef = useRef(null);
  const joinInFlightRef = useRef(false);
  const token = getAccessToken();

  const setJoinedTier = useCallback((tierId) => {
    joinedTierRef.current = tierId || null;
    setJoinedTierId(tierId || null);
  }, []);

  const navigateToMatch = useCallback((data) => {
    const gId = data?.matchId || data?.gameId;
    if (!gId || navigatingRef.current) return;
    navigatingRef.current = true;
    setActiveMatchId(gId);
    navigate(`/match/${gId}/countdown`, {
      state: {
        matchId: gId,
        gameType: data.gameType || 'whot',
        tierId: data.tierId || joinedTierRef.current,
        playerCount: data.playerCount,
        entryFee: data.entryFee,
        players: data.players || [],
      },
      replace: true,
    });
  }, [navigate]);

  const navigateToGame = useCallback((data) => {
    const gId = data?.matchId || data?.gameId;
    if (!gId || navigatingRef.current) return;
    const gameType = data?.gameType || 'whot';
    navigatingRef.current = true;
    setActiveMatchId(gId);
    navigate(`/play/${gameType}/${gId}`, {
      state: {
        matchSource: 'stake',
        matchId: gId,
        gameType,
        tierId: data?.tierId || joinedTierRef.current,
        playerCount: data?.playerCount,
        entryFee: data?.entryFee,
        players: data?.players || [],
      },
      replace: true,
    });
  }, [navigate]);

  // ============================================================
  // SOCKET.IO — Connect once on mount
  // ============================================================
  useEffect(() => {
    let cancelled = false;
    let socket = null;

    const connectSocket = async () => {
      let accessToken = getAccessToken();
      if (!accessToken) accessToken = await bootstrapAccessToken();
      if (!accessToken || cancelled) return;

      socket = io(SOCKET_URL, {
        auth: { token: accessToken },
        withCredentials: true,
        transports: ['websocket', 'polling'],
      });
      socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[StakePage] Socket connected');
      // Recover authoritative stake state after refresh/reconnect instead of
      // relying on the old React closure, which may contain null/stale state.
      api.stakes.getActive().then((res) => {
        const active = res.data?.data;
        if (!active) return;
        setJoinedTier(active.tierId);
        setActiveMatchId(active.matchId || null);
        setJoinedPlayersCurrent(active.currentPlayers || 1);
        setJoinedPlayersNeeded(active.maxPlayers || playerCount);
        socket.emit('stake:joined', { tierId: active.tierId });
        if (active.matchId && ['matched', 'countdown'].includes(active.state)) {
          navigateToMatch(active);
        } else if (active.matchId && active.state === 'in_progress') {
          // A refresh/reconnect on StakePage must recover an already-running
          // game instead of leaving the player stranded on the stake lobby.
          navigateToGame(active);
        }
      }).catch((err) => {
        console.warn('[StakePage] active stake recovery failed:', err);
      });
    });

    socket.on('connect_error', async (err) => {
      const message = String(err?.message || '').toLowerCase();
      if (message.includes('token') || message.includes('expired') || message.includes('authentication')) {
        try {
          const fresh = await refreshAccessToken();
          socket.auth = { token: fresh };
          socket.connect();
        } catch (_) {
          // API auth interceptor owns the final redirect if refresh fails.
        }
      }
    });

    // Listen for queue count updates (refresh tier listing)
    socket.on('stake:queueUpdated', () => {
      // Use the currently selected tab, not the playerCount captured when
      // this socket connection was created. Without this, joining a 4-player
      // tier could refresh the lobby with the initial 2-player listing.
      fetchStakesSilent(playerCountRef.current);
    });

    // ── WAITING UPDATES: show player count on the inline card ──
    socket.on('stake:waitingUpdate', (data) => {
      setJoinedPlayersCurrent(Number(data.currentPlayers ?? joinedPlayersCurrent));
      setJoinedPlayersNeeded(Number(data.maxPlayers ?? joinedPlayersNeeded));
    });

    // ── match:ready — show the full queue state before navigating ──
    const handleMatchReady = (data) => {
      console.log('[StakePage] match:ready', data);
      const hasBot = Boolean(data?.hasBot || data?.players?.some((p) => p.isBot));
      if (hasBot) {
        setJoinedPlayersCurrent(Number(data.currentPlayers || data.playerCount || joinedPlayersNeeded));
        setJoinedPlayersNeeded(Number(data.playerCount || joinedPlayersNeeded));
        if (!matchTransitionTimeoutRef.current) {
          matchTransitionTimeoutRef.current = setTimeout(() => {
            matchTransitionTimeoutRef.current = null;
            navigateToMatch(data);
          }, 1500);
        }
        return;
      }
      navigateToMatch(data);
    };
    socket.on('match:ready', handleMatchReady);

    // ── match:cancelled — reset card back to PLAY NOW ──
    socket.on('match:cancelled', (data) => {
      console.log('[StakePage] match:cancelled', data);
      setJoinedTier(null);
      setActiveMatchId(null);
      setCancelling(false);
      navigatingRef.current = false;
      fetchStakesSilent();
    });

    // ── When matched, preserve the short bot-filled queue transition ──
    socket.on('stake:matched', (data) => {
      console.log('[StakePage] stake:matched', data);
      handleMatchReady(data);
    });

    // ── Legacy cancelled ──
    socket.on('stake:cancelled', (data) => {
      setJoinedTier(null);
      setActiveMatchId(null);
      setCancelling(false);
      navigatingRef.current = false;
      fetchStakesSilent();
    });

    socket.on('stake:left', (data) => {
      console.log('[StakePage] stake:left', data);
      setJoinedTier(null);
      setActiveMatchId(null);
      setCancelling(false);
      navigatingRef.current = false;
      fetchStakesSilent();
    });

      return socket;
    };

    connectSocket();
    return () => {
      cancelled = true;
      if (matchTransitionTimeoutRef.current) clearTimeout(matchTransitionTimeoutRef.current);
      socket?.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line
  }, [token, navigateToMatch, setJoinedTier]);

  // ============================================================
  // FETCH STAKE TIERS
  // ============================================================
  async function fetchStakes() {
    setLoading(true);
    await fetchStakesSilent();
    setLoading(false);
  }

  async function fetchStakesSilent(requestedPlayerCount = playerCountRef.current) {
    try {
      const res = await api.get(`/stakes/${requestedPlayerCount}`);
      const nextTiers = res.data.tiers || [];
      setTiers(nextTiers);
      setError(null);
    } catch (err) {
      console.error('Fetch stakes error:', err);
      if (loading) {
        setError(err.response?.data?.error || err.message || 'Failed to load stakes');
        setTiers([]);
      }
    }
  }

  useEffect(() => {
    fetchStakes();

    // Load the existing public tournament feed so live tournaments are surfaced
    // above the normal stake tiers without changing tournament economics or rules.
    const loadLiveTournaments = async () => {
      try {
        const res = await api.tournaments.getAll({ status: 'live' });
        const list = res.data?.tournaments || res.data?.data || [];
        setTournaments(list.filter((t) => t.status === 'live'));
      } catch (err) {
        console.warn('[StakePage] tournament banner load failed:', err);
      }
    };
    loadLiveTournaments();
    const tournamentInterval = setInterval(() => {
      setTournamentNow(Date.now());
      loadLiveTournaments();
    }, 30000);
    // Socket updates are primary. Poll every 30 seconds as a recovery fallback
    // so the lobby self-heals even if a real-time event is missed.
    const interval = setInterval(fetchStakesSilent, 30000);
    return () => {
      clearInterval(tournamentInterval);
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, [playerCount]);

  // ============================================================
  // JOIN A TIER (now inline - no navigation to separate page)
  // ============================================================
  async function handleJoin(tier) {
    // Prevent double taps before React has committed the state update.
    if (joinInFlightRef.current || joinedTierRef.current) return;
    joinInFlightRef.current = true;
    setJoiningTier(tier.tierId);
    try {
      const res = await api.stakes.joinTier(tier.tierId);
      const data = res.data.data;

      // Register socket for this tier's lifecycle
      if (socketRef.current) {
        socketRef.current.emit('stake:joined', { tierId: tier.tierId });
      }

      // Update local state for inline card
      setJoinedTier(tier.tierId);
      setJoinedPlayersCurrent(Number(data.currentPlayers ?? 1));
      setJoinedPlayersNeeded(Number(data.maxPlayers ?? data.playerCount ?? playerCount));

    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to join stake';
      alert(msg);
    } finally {
      joinInFlightRef.current = false;
      setJoiningTier(null);
    }
  }

  // ============================================================
  // CANCEL (inline)
  // ============================================================
  function requestCancel(tierId) {
    if (cancelling) return;
    setCancelConfirmTierId(tierId);
  }

  async function handleCancel(tierId) {
    if (cancelling) return;
    setCancelConfirmTierId(null);
    setCancelling(true);

    try {
      const socket = socketRef.current;
      if (socket?.connected) {
        // Use the lifecycle socket as the authoritative cancellation path so
        // the server applies the correct refund rule for JOINED/MATCHED/
        // COUNTDOWN. HTTP is only the offline fallback.
        await new Promise((resolve, reject) => {
          // Use Socket.IO acknowledgement as the authoritative response.
          // The previous implementation waited for a broadcast event, which
          // could race with reconnect/navigation and incorrectly show a timeout
          // even though the server had already refunded the stake.
          const timer = setTimeout(() => reject(new Error('Cancellation request timed out. Please try again.')), 8000);
          socket.emit('stake:leave', { tierId }, (result) => {
            clearTimeout(timer);
            if (result?.success) resolve(result);
            else reject(new Error(result?.message || 'Cancellation failed'));
          });
        });
      } else {
        await api.stakes.cancelQueue();
      }

      setJoinedTier(null);
      setActiveMatchId(null);
      navigatingRef.current = false;
      fetchStakesSilent();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to cancel. Please try again.');
    } finally {
      setCancelling(false);
    }
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="jw-stake-page jw-stake-page--embedded" style={{ padding: 0, maxWidth: 'none', margin: '0 auto' }}>

      {/* Live Tournament — intentionally first content after the Stake header. */}
      {tournaments.length > 0 && (
        <section aria-label="Live Tournaments" style={{ marginBottom: '20px' }}>
          {tournaments.map((tournament) => {
            const endMs = new Date(tournament.end_time).getTime();
            const remainingMs = Math.max(0, endMs - tournamentNow);
            const totalSeconds = Math.floor(remainingMs / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const countdown = `${hours}h ${minutes}m ${String(seconds).padStart(2, '0')}s`;
            const rankRewards = Array.isArray(tournament.rank_rewards) ? tournament.rank_rewards : [];
            const firstPrize = Number(rankRewards.find((r) => Number(r.rank) === 1)?.amount || 0);
            const entryFee = Number(tournament.entry_fee || 0);
            return (
              <div key={tournament.id} className="jw-stake-tournament" style={{
                background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '14px',
                padding: '16px', marginBottom: '12px', boxSizing: 'border-box',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <strong style={{ color: '#fff', fontSize: '17px' }}>🏆 {tournament.name}</strong>
                      <span style={{ color: '#10b981', fontSize: '12px', fontWeight: 700 }}>● LIVE</span>
                    </div>
                    <div style={{ color: '#888', fontSize: '12px', marginTop: '5px' }}>
                      {tournament.game_type === 'whot' ? '🃏 Whot' : '🎮 Archived tournament'} • Closes in {remainingMs > 0 ? countdown : 'ending'}
                    </div>
                    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '10px', color: '#aaa', fontSize: '13px' }}>
                      <span>💰 Entry: <strong style={{ color: '#fff' }}>₦{entryFee.toLocaleString()}</strong></span>
                      <span>👥 Players: <strong style={{ color: '#fff' }}>{Number(tournament.current_players || 0)}/{Number(tournament.max_players || 0)}</strong></span>
                      <span>🎮 Plays: <strong style={{ color: '#fff' }}>{Number(tournament.max_plays_per_player || 0)}</strong></span>
                    </div>
                    {firstPrize > 0 && (
                      <div style={{ marginTop: '8px', color: '#f59e0b', fontSize: '13px' }}>
                        🏆 First Prize Pool: <strong>₦{firstPrize.toLocaleString()}</strong>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/tournaments/${tournament.id}`)}
                    style={{ padding: '10px 16px', background: '#00d4aa', color: '#000', border: 'none', borderRadius: '9px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {tournament.game_type === 'whot' ? 'PLAY / JOIN' : 'VIEW TOURNAMENT'}
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* 2P / 4P Toggle */}
      <div className="jw-stake-player-toggle" style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={() => { if (!joinedTierRef.current) setPlayerCount(2); }}
          disabled={!!joinedTierId}
          style={{
            padding: '10px 28px',
            borderRadius: '24px',
            border: 'none',
            background: playerCount === 2 ? '#00d4aa' : '#1a1a1a',
            color: playerCount === 2 ? '#000' : '#888',
            fontWeight: 'bold',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          2-PLAYER
        </button>
        <button
          onClick={() => { if (!joinedTierRef.current) setPlayerCount(4); }}
          disabled={!!joinedTierId}
          style={{
            padding: '10px 28px',
            borderRadius: '24px',
            border: 'none',
            background: playerCount === 4 ? '#00d4aa' : '#1a1a1a',
            color: playerCount === 4 ? '#000' : '#888',
            fontWeight: 'bold',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          4-PLAYER
        </button>
      </div>

      {/* Loading */}
      {loading && <Loading variant="section" label="" />}

      {/* Error */}
      {!loading && error && (
        <div style={{
          background: 'rgba(255,77,77,0.1)',
          border: '1px solid #ff4444',
          color: '#ff4444',
          padding: '16px',
          borderRadius: '12px',
          textAlign: 'center',
          marginBottom: '16px',
        }}>
          <p style={{ margin: 0, marginBottom: '12px' }}>{error}</p>
          <button
            onClick={fetchStakes}
            style={{
              background: '#ff4444',
              border: 'none',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {cancelConfirmTierId && (
        <PlatformConfirmModal
          title="Cancel Match?"
          message={"Are you sure you want to cancel this match?\nYour stake money will be refunded."}
          cancelLabel="Keep Match"
          confirmLabel="Cancel Match"
          onCancel={() => setCancelConfirmTierId(null)}
          onConfirm={() => handleCancel(cancelConfirmTierId)}
          busy={cancelling}
        />
      )}

      {/* Empty */}
      {!loading && !error && tiers.length === 0 && (
        <p style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
          No stakes available right now. Check back later.
        </p>
      )}

      {/* Tier Cards */}
      {!loading && tiers.filter(tier => tier.gameType === 'whot' && tier.isGameEnabled !== false).map((tier, idx) => (
        <div
          key={tier.tierId || idx}
          className="jw-stake-tier-card"
          style={{
            background: '#111111',
            borderRadius: '14px',
            padding: '16px',
            marginBottom: '12px',
            border: joinedTierId === tier.tierId ? '1px solid #00d4aa' : '1px solid #2a2a2a',
            transition: 'border-color 0.2s',
            boxShadow: joinedTierId === tier.tierId ? '0 0 12px rgba(0,212,170,0.15)' : 'none',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff' }}>
              {GAME_ICONS[tier.gameType] || GAME_ICONS[tier.game] || '🎮'}{' '}
              {GAME_NAMES[tier.gameType] || GAME_NAMES[tier.game] || 'TBD'}
            </span>
            <span style={{
              color: '#00d4aa',
              fontWeight: 'bold',
              fontSize: '18px',
            }}>
              ₦{Number(tier.entryFee).toLocaleString()}
            </span>
          </div>

          {/* Prize & Queue Info */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            marginTop: '10px', color: '#999', fontSize: '13px',
          }}>
            <span>
              🏆{' '}
              {playerCount === 2
                ? `₦${Number(tier.prizePool || 0).toLocaleString()}`
                : tier.firstPrize
                  ? `1st: ₦${Number(tier.firstPrize).toLocaleString()} | 2nd: ₦${Number(tier.secondPrize || 0).toLocaleString()}`
                  : `₦${Number(tier.prizePool || 0).toLocaleString()}`
              }
            </span>
            <span style={{ color: (joinedTierId === tier.tierId ? joinedPlayersCurrent : tier.waitingCount) > 0 ? '#00d4aa' : '#666' }}>
              {joinedTierId === tier.tierId
                ? `${joinedPlayersCurrent}/${joinedPlayersNeeded} waiting`
                : `${tier.waitingCount || 0}/${tier.playerCount || playerCount} waiting`}
            </span>
          </div>

          {joinedTierId === tier.tierId ? (
            <div style={{ marginTop: '7px', color: joinedPlayersCurrent >= joinedPlayersNeeded ? '#00d4aa' : '#999', fontSize: '12px', fontWeight: 700, letterSpacing: '0.3px' }}>
              {getWaitingLabel(joinedPlayersCurrent, joinedPlayersNeeded)}
            </div>
          ) : (
            <div style={{
              marginTop: '7px',
              color: Number(tier.waitingCount || 0) >= Number(tier.playerCount || playerCount) ? '#00d4aa' : '#999',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.3px',
            }}>
              {getWaitingLabel(Number(tier.waitingCount || 0), Number(tier.playerCount || playerCount))}
            </div>
          )}

          {/* Action Area: Inline buttons for joined tier, PLAY NOW for others */}
          {joinedTierId === tier.tierId ? (
            // ── Joined State: Show Cancel + Joined buttons inline ──
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button
                onClick={() => requestCancel(tier.tierId)}
                disabled={cancelling}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#331a1a',
                  border: '1px solid #ff5555',
                  borderRadius: '8px',
                  color: '#ff5555',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: cancelling ? 'not-allowed' : 'pointer',
                  opacity: cancelling ? 0.5 : 1,
                }}
              >
                {cancelling ? 'Cancelling...' : 'Cancel'}
              </button>
              <button
                disabled
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#1a3328',
                  border: '1px solid #00c896',
                  borderRadius: '8px',
                  color: '#00c896',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#00c896',
                  animation: 'pulse 1.2s ease-in-out infinite',
                }} />
                Joined ({joinedPlayersCurrent}/{joinedPlayersNeeded})
              </button>
            </div>
          ) : (
            // ── Non-Joined State: PLAY NOW ──
            <button
              onClick={() => handleJoin(tier)}
              disabled={joiningTier === tier.tierId || tier.isGameEnabled === false || !!joinedTierId}
              style={{
                width: '100%',
                marginTop: '14px',
                padding: '12px',
                background: joiningTier === tier.tierId || tier.isGameEnabled === false || joinedTierId
                  ? '#555' : '#00d4aa',
                border: 'none',
                borderRadius: '10px',
                color: joiningTier === tier.tierId || tier.isGameEnabled === false || joinedTierId
                  ? '#999' : '#000',
                fontWeight: 'bold',
                fontSize: '15px',
                cursor: joiningTier === tier.tierId || tier.isGameEnabled === false || joinedTierId
                  ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: tier.isGameEnabled === false ? 0.4 : 1,
              }}
            >
              {tier.isGameEnabled === false
                ? 'DISABLED'
                : (joiningTier === tier.tierId
                  ? 'JOINING...'
                  : (joinedTierId
                    ? getWaitingLabel(tier.waitingCount || 0, tier.playerCount || playerCount)
                    : 'PLAY NOW'))}
            </button>
          )}
        </div>
      ))}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}